import { TElement } from "@udecode/plate-common";
import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import {
    TournamentSeriesForm,
    convertFormToGraphQL,
    convertGraphQLToForm,
} from "@/components/forms/TournamentSeriesForm";
import { TournamentSeriesInput } from "@/components/forms/validation/tournament-series";
import { graphql } from "@/gen";

const tournamentSeriesDetailQuery = graphql(`
    query EditTournamentSeriesDetail($id: UUID!) {
        tournamentSeries(id: $id) {
            id
            title
            description
            startAt
            endAt
            isInternalOnly
            isHidden
            heroImageId
            thumbnailImageId
            previousWinnerPlayerId
            standingsInfo
            standingsCalculation
        }
    }
`);

const updateTournamentSeriesMutation = graphql(`
    mutation updateTournamentSeries($input: UpdateTournamentSeriesInput!) {
        updateTournamentSeries(input: $input) {
            id
            title
            standingsInfo
        }
    }
`);

export default function EditTournamentSeriesRoute() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [queryResult] = useQuery({
        query: tournamentSeriesDetailQuery,
        variables: { id: id! },
        pause: !id,
    });

    const [_, updateTournamentSeries] = useMutation(updateTournamentSeriesMutation);

    const handleSubmit: SubmitHandler<TournamentSeriesInput> = useCallback(
        async data => {
            try {
                const res = await updateTournamentSeries({
                    input: {
                        id: id!,
                        ...convertFormToGraphQL(data),
                    },
                });
                if (res.error) {
                    res.error.graphQLErrors.forEach(error => {
                        toast.error("Error updating tournament series", {
                            description: error.message,
                        });
                    });
                } else {
                    toast.success("Tournament series updated successfully");
                    navigate(`/tournament-series/${id}`);
                }
            } catch (err) {
                toast.error("Request error", { description: String(err) });
            }
        },
        [updateTournamentSeries, navigate, id]
    );

    if (!id) return <div>No tournament series ID provided.</div>;
    if (queryResult.fetching) return <div>Loading...</div>;
    if (queryResult.error) return <div>Error: {queryResult.error.message}</div>;

    const series = queryResult.data?.tournamentSeries;
    if (!series) return <div>Tournament series not found.</div>;

    const defaultValues: TournamentSeriesInput = {
        title: series.title,
        description: series.description as TElement[] | null,
        startAt: new Date(series.startAt),
        endAt: new Date(series.endAt),
        heroImageId: series.heroImageId,
        thumbnailImageId: series.thumbnailImageId,
        previousWinnerPlayerId: series.previousWinnerPlayerId,
        isInternalOnly: series.isInternalOnly,
        isHidden: series.isHidden,
        standingsInfo: series.standingsInfo,
        standingsCalculation: convertGraphQLToForm(series.standingsCalculation),
    };

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader title="Edit tournament series" />
            <TournamentSeriesForm
                onSubmit={handleSubmit}
                defaultValues={defaultValues}
                submitText="Edit tournament series"
            />
        </div>
    );
}
